import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useLang } from "../LanguageContext";
import { IMAGES } from "../content";
import { Reveal } from "./Reveal";

export const About = () => {
  const { t } = useLang();
  const a = t.about;
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y1 = useTransform(scrollYProgress, [0, 1], ["-6%", "6%"]);
  const y2 = useTransform(scrollYProgress, [0, 1], ["6%", "-6%"]);

  return (
    <section id="about" ref={ref} className="bg-ivory py-24 md:py-36" data-testid="about-section">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          <div className="lg:col-span-5">
            <Reveal>
              <p className="text-olive text-xs uppercase tracking-[0.35em] mb-8">{a.chapter}</p>
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="font-serif text-brown text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-balance">
                {a.title}
              </h2>
            </Reveal>
            <div className="mt-10 space-y-10">
              {a.story.map((chapter, i) => (
                <Reveal key={chapter.h} delay={0.15 + i * 0.05}>
                  <div>
                    {i > 0 && (
                      <h3 className="font-serif text-brown text-2xl md:text-3xl mb-4">{chapter.h}</h3>
                    )}
                    <div className="space-y-4">
                      {chapter.p.map((para, j) => (
                        <p key={j} className="text-brown/70 text-base md:text-lg font-light leading-loose">{para}</p>
                      ))}
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>

          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-5 lg:sticky lg:top-28">
            <Reveal delay={0.15} y={50}>
              <figure className="group">
                <div className="relative aspect-[3/4] overflow-hidden rounded-xl shadow-lg shadow-brown/10">
                  <motion.img
                    style={{ y: y1, scale: 1.12, objectPosition: "center 40%" }}
                    src={IMAGES.about.exterior}
                    alt="Exterior of Restaurante São João in Comporta"
                    loading="lazy"
                    className="h-full w-full object-cover"
                  />
                </div>
                <figcaption className="mt-3 text-brown/50 text-xs uppercase tracking-[0.2em]">{a.captionExterior}</figcaption>
              </figure>
            </Reveal>
            <Reveal delay={0.28} y={50}>
              <figure className="group sm:mt-12">
                <div className="relative aspect-[3/4] overflow-hidden rounded-xl shadow-lg shadow-brown/10">
                  <motion.img
                    style={{ y: y2, scale: 1.12 }}
                    src={IMAGES.about.dining}
                    alt="Elegant dining room at Restaurante São João"
                    loading="lazy"
                    className="h-full w-full object-cover"
                  />
                </div>
                <figcaption className="mt-3 text-brown/50 text-xs uppercase tracking-[0.2em]">{a.captionDining}</figcaption>
              </figure>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};
